/**
 * Slots — set the day's number of slots in one go (user, 20/10/2026: "a
 * way to say okay I want 20 slots, directly"): type it or step it, and one
 * button adds the difference (each new slot takes the next group in turn,
 * as Add drop does) or removes the last ones of the list. Nothing changes
 * until that button, and nothing is saved until Save container.
 * For Media Devoted | Created By Ciro Autiero
 */
import { FC, useEffect, useState } from 'react';
import { Rows3 } from 'lucide-react';

import { Button } from '@/components/ui';

import { FILL_SLOT_CAP } from '../../../../../utils/drops/fill-count';
import type { ContainerEditor } from '../../../state/useContainerEditor';
import { NumberStepper } from '../../controls/NumberStepper';
import { StepRow } from '../../controls/StepRow';
import { type CountPlan, countPlan, countWords, SLOT_MAX } from './slot-count';

const HINT =
  'How many slots the day has. Set a number, then one click adds the difference — each new slot takes the next group in turn — or removes the last slots of the list (pinned ones last). Nothing is saved until Save container.';

function applyPlan(editor: ContainerEditor, plan: Exclude<CountPlan, null>) {
  if ('remove' in plan) return editor.slotsRemove(plan.remove);
  for (let left = plan.add; left > 0; left -= FILL_SLOT_CAP) editor.slotAddMany(Math.min(left, FILL_SLOT_CAP));
}

export const SlotCountRow: FC<{ editor: ContainerEditor; timeOrder: number[] }> = ({ editor, timeOrder }) => {
  const count = editor.draft.slots.length;
  const [target, setTarget] = useState(count);
  useEffect(() => setTarget(count), [count]); // follows Add drop, ✕, Fill and the delete button
  const plan = countPlan(editor.draft.slots, target, timeOrder);
  return (
    <StepRow icon={Rows3} label="Slots" hint={HINT}>
      <div className="flex flex-wrap items-center gap-x-4 gap-y-2">
        <NumberStepper value={target} onChange={setTarget} min={0} max={SLOT_MAX} step={1} ariaLabel="Slots in the day" unit="slots" />
        {plan ? (
          <span className="flex items-center gap-2">
            <Button size="sm" variant={'add' in plan ? 'default' : 'outline'} className={'add' in plan ? 'h-8' : 'h-8 border-destructive/40 text-destructive hover:bg-destructive/10'} onClick={() => applyPlan(editor, plan)}>
              {countWords(plan)}
            </Button>
            <button type="button" onClick={() => setTarget(count)} className="text-xs text-muted-foreground hover:text-foreground hover:underline">
              Keep {count}
            </button>
          </span>
        ) : null}
      </div>
    </StepRow>
  );
};
